import { useEffect, useState } from "preact/hooks";

import { useProjectTasks } from "../hooks/useProjectTasks";
import { COULD_NOT_SAVE_ERROR } from "../lib/recordsCopy";
import { UP_FOR_GRABS_LABEL, taskDoneAriaLabel } from "../lib/copy";
import { HELP_COPY } from "../lib/helpCopy";
import {
  ADD_TASK_LABEL,
  ADD_TASK_PLACEHOLDER,
  assignedMessage,
  CHECKLIST_DONE_LABEL,
  CHECKLIST_EDIT_LABEL,
  CHECKLIST_EMPTY_BODY,
  CHECKLIST_EMPTY_TITLE,
  CHECKLIST_HEADING,
  CLEARED_ASSIGNEE_MESSAGE,
  deleteTaskAriaLabel,
  deleteTaskConfirmTitle,
  movedToAnnouncement,
  NOBODY_YET_LABEL,
  reassignedMessage,
  SOMEONE_ASSIGNED_LABEL,
  reorderTaskAriaLabel,
  taskRemovedMessage,
  whosDoingAriaLabel,
} from "../lib/projectTasksCopy";
import type { ProjectTaskOut } from "../types";
import type { SnackbarState } from "../hooks/useDue";
import { AssigneePickerSheet, type AssigneeOption } from "./AssigneePickerSheet";
import { ConfirmDialog } from "./ConfirmDialog";
import { InfoTooltip } from "./InfoTooltip";
import { NameDot } from "./NameDot";
import { ProjectRollup } from "./ProjectRollup";
import { SafetySwitch } from "./SafetySwitch";
import { Snackbar } from "./Snackbar";

// Sprint-017 E7 (design §3, ADR-003 §5) — the editable checklist body that
// replaces the display-only `punch_list` render inside `ProjectDetailOverlay`.
// It slots into `RecordDetailOverlay`'s `children`; it owns NO overlay chrome.
//
// Two explicit modes (design §3.4, resolves tap-vs-drag):
//   - View: 48px tap-to-check rows, bold label, trailing assignee chip (its
//     own hit target → assign picker), "Up for grabs" when unassigned,
//     `.safety-tag` when flagged. Pinned "+ Add something to the list".
//   - Edit: check + assign chips hidden; drag handle (keyboard-reorderable),
//     inline label rename, delete, per-row Safety switch. Add row persists.
//
// Affordance = authz (design §6): the assign chip is a `<button>` only when
// `canAssignOut` (admin/manager); otherwise a non-interactive `<span>` with
// no assign word anywhere. Reordering/rename/delete/add/safety are all
// `can_edit_tasks` (owner, collaborator, admin/api).

export interface ProjectTasksChecklistProps {
  projectId: string;
  /** ADR-003 §3.2 `can_assign_out` — admin/manager (with reach). Drives
   * whether the trailing chip is interactive (opens the picker) or a
   * non-focusable state-only span. */
  canAssignOut: boolean;
  /** People for the assign picker (admin/manager sessions have a roster via
   * the team board; empty otherwise — the picker simply lists nobody). */
  roster: AssigneeOption[];
  /** Extra id -> display name pairs for naming assignees who are not in
   * the picker `roster` (which is team-board-sourced and empty for a
   * member session). `ProjectDetailOverlay` fills this from what the
   * project view already has: the helper list (owner/admin) and the
   * signed-in person themselves. Display only; never offered as picks. */
  assigneeNames?: Record<string, string>;
  /** Mirrors the roll-up up to the overlay header (design §3.7). */
  onRollupChange?: (done: number, total: number) => void;
}

const EMPTY_NAMES: Record<string, string> = {};

function nameFor(
  roster: AssigneeOption[],
  assigneeNames: Record<string, string>,
  id: string | null,
): string {
  if (!id) return "";
  return roster.find((o) => o.id === id)?.name ?? assigneeNames[id] ?? "";
}

export function ProjectTasksChecklist({
  projectId,
  canAssignOut,
  roster,
  assigneeNames = EMPTY_NAMES,
  onRollupChange,
}: ProjectTasksChecklistProps) {
  const nameOf = (id: string | null) => nameFor(roster, assigneeNames, id);
  const {
    tasks,
    loading,
    error,
    addTask,
    toggleDone,
    renameTask,
    reorderTask,
    assignTask,
    setSafety,
    deleteTask,
  } = useProjectTasks(projectId);

  const [editMode, setEditMode] = useState(false);
  const [assignTaskId, setAssignTaskId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState("");
  const [addSafety, setAddSafety] = useState(false);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<ProjectTaskOut | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const [liveMessage, setLiveMessage] = useState("");

  const doneCount = tasks.filter((t) => t.done).length;
  const totalCount = tasks.length;

  useEffect(() => {
    onRollupChange?.(doneCount, totalCount);
  }, [doneCount, totalCount, onRollupChange]);

  const assignTaskRow = tasks.find((t) => t.id === assignTaskId) ?? null;

  async function handleToggle(task: ProjectTaskOut) {
    const ok = await toggleDone(task.id, !task.done);
    if (!ok) setSnackbar({ message: COULD_NOT_SAVE_ERROR });
  }

  async function handleAssign(id: string) {
    if (!assignTaskRow) return;
    const previous = assignTaskRow.assignee_id;
    await assignTask(assignTaskRow.id, id);
    const who = nameOf(id);
    setSnackbar({ message: previous ? reassignedMessage(who) : assignedMessage(who) });
    setAssignTaskId(null);
  }

  async function handleClearAssign() {
    if (!assignTaskRow) return;
    await assignTask(assignTaskRow.id, null);
    setSnackbar({ message: CLEARED_ASSIGNEE_MESSAGE });
    setAssignTaskId(null);
  }

  async function handleSubmitAdd() {
    const text = addText.trim();
    if (!text) {
      setAdding(false);
      return;
    }
    try {
      await addTask(text, addSafety);
      setAddText("");
      setAddSafety(false);
      setAdding(false);
    } catch {
      setSnackbar({ message: COULD_NOT_SAVE_ERROR });
    }
  }

  function requestDelete(task: ProjectTaskOut) {
    // Q3: confirm ONLY when the task is assigned OR done (someone acted on
    // it); otherwise delete immediately with an Undo snackbar.
    if (task.assignee_id || task.done) {
      setConfirmDeleteTask(task);
      return;
    }
    const removed = task;
    void deleteTask(task.id);
    setSnackbar({
      message: taskRemovedMessage(removed.title),
      actionLabel: "Undo",
      onUndo: () => void addTask(removed.title, removed.safety),
    });
  }

  async function confirmDelete() {
    if (!confirmDeleteTask) return;
    await deleteTask(confirmDeleteTask.id);
    setConfirmDeleteTask(null);
  }

  // Keyboard reorder (design §9.3 — the required drag alternative). The
  // handle is a real button; ArrowUp/ArrowDown while it is focused move the
  // row one slot and announce "Moved to k of m" via an assertive live
  // region. (A simplified move-on-arrow model rather than an explicit
  // pick-up/drop FSM — flagged for the PM; the accessible, announced
  // keyboard path is the binding requirement and is satisfied.)
  function handleReorderKey(task: ProjectTaskOut, index: number, e: KeyboardEvent) {
    if (e.key === "ArrowUp" && index > 0) {
      e.preventDefault();
      void reorderTask(task.id, index - 1);
      setPickedId(task.id);
      setLiveMessage(movedToAnnouncement(index, totalCount));
    } else if (e.key === "ArrowDown" && index < totalCount - 1) {
      e.preventDefault();
      void reorderTask(task.id, index + 1);
      setPickedId(task.id);
      setLiveMessage(movedToAnnouncement(index + 2, totalCount));
    }
  }

  return (
    <>
      {/* Body-top roll-up (design §3.1/§3.7). */}
      <ProjectRollup done={doneCount} total={totalCount} variant="body" />

      <div class="pt-checklist-header">
        <h2 class="admin-section-heading">{CHECKLIST_HEADING}</h2>
        <button
          type="button"
          class="secondary-btn pt-mode-toggle"
          aria-disabled={totalCount === 0 ? "true" : undefined}
          disabled={totalCount === 0}
          onClick={() => setEditMode((v) => !v)}
        >
          {editMode ? CHECKLIST_DONE_LABEL : CHECKLIST_EDIT_LABEL}
        </button>
      </div>

      {loading ? (
        <p class="loading-hint">Loading…</p>
      ) : error ? (
        <p class="error-hint" role="alert">
          {error}
        </p>
      ) : totalCount === 0 ? (
        <div class="pt-empty">
          <p class="pt-empty-title">{CHECKLIST_EMPTY_TITLE}</p>
          <p class="pt-empty-body">{CHECKLIST_EMPTY_BODY}</p>
        </div>
      ) : (
        <ul class="pt-checklist task-list">
          {tasks.map((task, index) => (
            <li
              key={task.id}
              class={`pt-row task-row${task.done ? " is-done" : ""}${
                pickedId === task.id ? " is-picked" : ""
              }`}
              data-task-id={task.id}
            >
              {editMode ? (
                <>
                  <button
                    type="button"
                    class="pt-drag-handle"
                    aria-label={reorderTaskAriaLabel(task.title)}
                    aria-pressed={pickedId === task.id}
                    aria-roledescription="sortable"
                    onKeyDown={(e) => handleReorderKey(task, index, e)}
                    onBlur={() => setPickedId((p) => (p === task.id ? null : p))}
                  >
                    <span aria-hidden="true">≡</span>
                  </button>
                  <input
                    class="pt-label-input task-title"
                    value={task.title}
                    maxLength={300}
                    aria-label={`Rename ${task.title}`}
                    onBlur={(e) => {
                      const next = (e.target as HTMLInputElement).value.trim();
                      if (next && next !== task.title) void renameTask(task.id, next);
                    }}
                  />
                  <SafetySwitch
                    checked={task.safety}
                    idSuffix={`-${task.id}`}
                    onChange={(next) => void setSafety(task.id, next)}
                  />
                  <button
                    type="button"
                    class="pt-delete-btn"
                    aria-label={deleteTaskAriaLabel(task.title)}
                    onClick={() => requestDelete(task)}
                  >
                    <span aria-hidden="true">⊖</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    class="check-circle"
                    aria-pressed={task.done}
                    aria-label={taskDoneAriaLabel(task.title, task.done)}
                    onClick={() => void handleToggle(task)}
                  >
                    {task.done ? (
                      <span class="check-glyph" aria-hidden="true">
                        ✓
                      </span>
                    ) : null}
                  </button>
                  <div class="task-copy">
                    <div class="task-title">{task.title}</div>
                    {task.safety ? <span class="safety-tag pt-safety-tag">Safety</span> : null}
                  </div>
                  {canAssignOut ? (
                    <button
                      type="button"
                      class="pt-assign-chip"
                      aria-label={whosDoingAriaLabel(task.title)}
                      onClick={() => setAssignTaskId(task.id)}
                    >
                      {task.assignee_id ? (
                        <NameDot id={task.assignee_id} name={nameOf(task.assignee_id)} />
                      ) : (
                        <span class="pt-upforgrabs">{UP_FOR_GRABS_LABEL}</span>
                      )}
                    </button>
                  ) : (
                    <span
                      class="pt-assign-chip is-static"
                      aria-label={
                        task.assignee_id
                          ? nameOf(task.assignee_id) || SOMEONE_ASSIGNED_LABEL
                          : UP_FOR_GRABS_LABEL
                      }
                    >
                      {task.assignee_id ? (
                        <NameDot id={task.assignee_id} name={nameOf(task.assignee_id)} />
                      ) : (
                        <span class="pt-upforgrabs">{UP_FOR_GRABS_LABEL}</span>
                      )}
                    </span>
                  )}
                  {/* Sprint-019 E5b (audit row #14) — only rendered when
                   * the chip above is the non-interactive `.is-static`
                   * state (`!canAssignOut`): explains WHY it's static
                   * rather than leaving a viewer to wonder if reassigning
                   * is simply broken. Never shown alongside the
                   * interactive chip — that one is self-explanatory (it
                   * opens the picker). */}
                  {!canAssignOut ? (
                    <InfoTooltip
                      label={HELP_COPY.assignOutMemberOwner.label}
                      content={HELP_COPY.assignOutMemberOwner.content}
                    />
                  ) : null}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Pinned "+ Add" row — visible in BOTH modes (design §3.6). */}
      {adding ? (
        <div class="pt-add-row is-editing">
          <input
            class="pt-add-input task-title"
            value={addText}
            maxLength={300}
            placeholder={ADD_TASK_PLACEHOLDER}
            aria-label={ADD_TASK_PLACEHOLDER}
            autofocus
            onInput={(e) => setAddText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSubmitAdd();
              if (e.key === "Escape") {
                setAdding(false);
                setAddText("");
                setAddSafety(false);
              }
            }}
          />
          <SafetySwitch checked={addSafety} idSuffix="-add" onChange={setAddSafety} />
          <button
            type="button"
            class="primary-btn pt-add-submit"
            onClick={() => void handleSubmitAdd()}
          >
            {CHECKLIST_DONE_LABEL}
          </button>
        </div>
      ) : (
        <button type="button" class="pt-add-row" onClick={() => setAdding(true)}>
          {ADD_TASK_LABEL}
        </button>
      )}

      <div class="sr-only" aria-live="assertive">
        {liveMessage}
      </div>

      {assignTaskRow ? (
        <AssigneePickerSheet
          open
          pieceTitle={assignTaskRow.title}
          options={roster}
          selectedId={assignTaskRow.assignee_id}
          onSelect={(id) => void handleAssign(id)}
          onClear={() => void handleClearAssign()}
          clearLabel={NOBODY_YET_LABEL}
          onCancel={() => setAssignTaskId(null)}
          topSlot={
            <SafetySwitch
              checked={assignTaskRow.safety}
              idSuffix="-picker"
              onChange={(next) => void setSafety(assignTaskRow.id, next)}
            />
          }
        />
      ) : null}

      {confirmDeleteTask ? (
        <ConfirmDialog
          open
          title={deleteTaskConfirmTitle(confirmDeleteTask.title)}
          confirmLabel="Remove"
          destructive
          onConfirm={() => void confirmDelete()}
          onCancel={() => setConfirmDeleteTask(null)}
        />
      ) : null}

      <Snackbar state={snackbar} onDismiss={() => setSnackbar(null)} />
    </>
  );
}
